import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import type { AttendanceRecord } from '@tailoring-crm/shared';
import { formatMinutes, formatTime } from '../../lib/format.js';
import { Button } from '@tailoring-crm/ui';
import { AttendanceStatusBadge } from './attendance-status-badge.js';

/** What an empty cell reads as. A blank one is read as a bug. */
const NOTHING = '—';

/**
 * One person's day.
 *
 * THE HOURS CELL IS THE SERVER'S `workedMinutes` AND NOTHING ELSE. It would be one line to
 * subtract the arrival from the departure here, and that line would be wrong the first time
 * somebody stepped out at lunch and back in again, or worked past midnight, or had their day
 * corrected by a manager. The server owns the arithmetic; this cell owns the punctuation.
 *
 * Which buttons exist is decided by the status, not by disabling a fixed set: a completed day has
 * no check-in to offer, and a greyed-out button is a promise that something could happen.
 */
export interface AttendanceRowProps {
  record: AttendanceRecord;
  /** False for a karigar looking at their own row — they may read the day, not write it. */
  canWrite: boolean;
  /** A write is in flight for this person; the buttons stop taking a second press. */
  pending?: boolean;
  onCheckIn: (record: AttendanceRecord) => void;
  onCheckOut: (record: AttendanceRecord) => void;
  onMarkLeave: (record: AttendanceRecord) => void;
  onViewHistory: (record: AttendanceRecord) => void;
}

export function AttendanceRow({
  record,
  canWrite,
  pending = false,
  onCheckIn,
  onCheckOut,
  onMarkLeave,
  onViewHistory,
}: AttendanceRowProps): ReactElement {
  const { t } = useTranslation();

  const mayCheckIn = canWrite && record.status === 'not_started';
  const mayCheckOut = canWrite && record.status === 'working';
  const mayMarkLeave = canWrite && (record.status === 'not_started' || record.status === 'working');

  return (
    <tr className="border-t border-border align-top" data-testid="attendance-row">
      <th scope="row" className="py-2 pr-3 text-left font-normal">
        <Link to={`/staff/${record.staffUserId}`} className="font-medium hover:underline">
          {record.staffName}
        </Link>
        <span className="block text-xs text-muted-foreground">{record.staffEmail}</span>
      </th>
      <td className="py-2 pr-3">{t(`role.${record.staffRole}`)}</td>
      <td className="py-2 pr-3">
        <AttendanceStatusBadge status={record.status} late={record.late} halfDay={record.halfDay} />
      </td>
      <td className="py-2 pr-3 tabular-nums">
        {record.checkInAt ? formatTime(record.checkInAt) : NOTHING}
      </td>
      <td className="py-2 pr-3 tabular-nums">
        {record.checkOutAt ? formatTime(record.checkOutAt) : NOTHING}
      </td>
      <td className="py-2 pr-3 tabular-nums">{formatMinutes(record.workedMinutes)}</td>
      <td className="py-2">
        <div className="flex flex-wrap gap-2">
          {mayCheckIn ? (
            <Button size="sm" disabled={pending} onClick={() => onCheckIn(record)}>
              {t('attendance.action.checkIn')}
            </Button>
          ) : null}
          {mayCheckOut ? (
            <Button size="sm" disabled={pending} onClick={() => onCheckOut(record)}>
              {t('attendance.action.checkOut')}
            </Button>
          ) : null}
          {mayMarkLeave ? (
            <Button
              size="sm"
              variant="outline"
              disabled={pending}
              onClick={() => onMarkLeave(record)}
            >
              {t('attendance.action.markLeave')}
            </Button>
          ) : null}
          {/* Reading a person's own month back is not a write, so it is offered to everybody the
              screen is offered to at all — including the karigar whose row this is. */}
          <Button size="sm" variant="ghost" onClick={() => onViewHistory(record)}>
            {t('attendance.action.viewHistory')}
          </Button>
        </div>
      </td>
    </tr>
  );
}

export default AttendanceRow;
